import { FUNDING, PayPalButtons } from '@paypal/react-paypal-js';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { StoreInterface } from '../../../store/types';
import { PaymentMethod, PaymentStatus } from '../../../services/exports/Constants';
import { PaymentResult } from '../../../services/exports/Interfaces';
import PaymentManager from '../../../services/api/PaymentManager';
import OrderManager from '../../../services/api/OrderManager';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import useOrderCheckout from '../../../hooks/payment/useOrderCheckout';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import paymentLog from '../../../services/helpers/PaymentLogger';

/**
 * PayPal-direct (paypal_standalone) SDK buttons — the content of the PayPal row
 * when the variant router resolves to `direct`. The order is drafted/captured as
 * paypal_standalone (direct — settles to the restaurant) and PayPal captures server-side on approval.
 *
 * The PayPalScriptProvider is mounted higher up (PaymentInfoSection) so the SDK
 * loads once and isn't torn down on selection.
 */
export default function PaypalDirectButtons() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaypalStandaloneSection' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { data, getOrderData } = useOrderFlow();
  const { isPaymentProcessing, setIsPaymentProcessing } = usePaymentCoordinator();
  const { checkout } = useOrderCheckout({
    setLoading: setIsPaymentProcessing,
    onMobileAppCheckoutError: () => setIsPaymentProcessing(false),
  });

  const clientId = company?.paypal_standalone?.client_id;

  if (!clientId) {
    return null;
  }

  // Attached to every PayPal-direct log call. payment_method is promoted to a Sentry tag
  // on error logs (see PaymentLogger), matching how Stripe tags `payment_method`.
  const logAttrs = {
    company_id: company?.id,
    payment_method: PaymentMethod.PaypalStandalone,
    currency: company?.currency,
  };

  // Server captures during checkout, so the client-side confirm is a no-op.
  const confirmPayment = async (): Promise<PaymentResult> => ({
    status: PaymentStatus.Succeeded,
    error: null,
    result: null,
  });

  const createOrder = async (): Promise<string> => {
    paymentLog.info('paypal', 'create_order_start', {
      ...logAttrs,
      cart_total: data.total,
    });

    // Draft as paypal_standalone so the PayPal order amount equals the
    // server-authoritative total for this method (the server decides whether it
    // carries a service fee: partner restaurants charge one, others do not),
    // which the capture-time integrity guard reconciles against.
    const draft = await OrderManager.draftOrder(getOrderData({ payment_method_id: PaymentMethod.PaypalStandalone }));

    if (!draft.success) {
      paymentLog.error('paypal', 'create_order_draft_failed', {
        ...logAttrs,
        status: draft.status,
        error_code: (draft.response as any)?.response?.data?.error_code,
        message: (draft.response as any)?.message,
      });

      // Fail closed: never fall back to a client-side total. A mismatched amount
      // would be rejected by the capture-time integrity guard anyway, so abort
      // before any PayPal order is created rather than start a doomed payment.
      throw new Error('Failed to draft PayPal order');
    }

    const amount = draft.response.data.data.total;

    const { success, response, status } = await PaymentManager.createPaypalStandaloneOrder({
      company_id: company.id,
      amount,
    });

    if (!success) {
      paymentLog.error('paypal', 'create_order_failed', {
        ...logAttrs,
        amount,
        status,
        error_code: (response as any)?.response?.data?.error_code,
        message: (response as any)?.message,
      });

      throw new Error('Failed to create PayPal order');
    }

    paymentLog.info('paypal', 'create_order_succeeded', {
      ...logAttrs,
      amount,
      paypal_order_id: response.data.id,
    });

    return response.data.id;
  };

  // The capture is gated behind OTP server-side: an OTP-required order POST returns
  // before any capture, and only the OTP-verified retry captures. The UX wrinkle is
  // that the OTP modal would otherwise open under PayPal's own overlay while onApprove
  // is pending (modal unclickable + PayPal stuck loading). So — mirroring the Adyen
  // wallet flow — resolve onApprove right before the OTP modal opens, which dismisses
  // PayPal's overlay and lets the OTP modal take over while checkout/capture continues.
  const onApprove = (approveData: { orderID: string }): Promise<void> =>
    new Promise<void>((resolveApprove) => {
      paymentLog.info('paypal', 'approved', {
        ...logAttrs,
        paypal_order_id: approveData.orderID,
      });

      void checkout(
        confirmPayment,
        undefined,
        {
          payment_method_id: PaymentMethod.PaypalStandalone,
          paypal_order_id: approveData.orderID,
        },
        undefined,
        { onBeforeOtpModalOpen: () => resolveApprove() },
      ).finally(() => resolveApprove());
    });

  const onError = (error?: Record<string, unknown>): void => {
    paymentLog.error('paypal', 'sdk_error', {
      ...logAttrs,
      message: typeof error?.message === 'string' ? error.message : undefined,
    });
    setIsPaymentProcessing(false);
    toast.error(t('error'));
  };

  const onCancel = (cancelData?: { orderID?: string }): void => {
    paymentLog.info('paypal', 'cancelled', {
      ...logAttrs,
      paypal_order_id: cancelData?.orderID,
    });
    setIsPaymentProcessing(false);
  };

  return (
    <PayPalButtons
      forceReRender={[clientId, company?.currency, data.total]}
      disabled={isPaymentProcessing}
      // Render only the PayPal button — we already expose card payment as its own
      // method, so suppress the SDK's card/Pay-Later funding sources. `shape: 'pill'`
      // matches the app's rounded-full primary button; no wrapper padding since the
      // button already sits inside the order card's padding.
      fundingSource={FUNDING.PAYPAL}
      style={{ layout: 'vertical', label: 'pay', color: 'black', shape: 'pill' }}
      createOrder={createOrder}
      onApprove={onApprove}
      onError={onError}
      onCancel={onCancel}
    />
  );
}
